import './App.css';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Gamepad2, TrendingUp, Trophy, Target, Lightbulb, Star, Share2, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

function App() {
  const [riotId, setRiotId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!riotId.includes('#')) {
      setError('Enter Riot ID as GameName#TagLine');
      return;
    }
    setLoading(true);
    try {
      const apiUrl = 'https://2ycjgf3vfa.execute-api.us-east-1.amazonaws.com/dev';
      const res = await fetch(`${apiUrl}/rewind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ riotId })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `API error: ${res.status}`);
      }
      const data = await res.json();
      console.log('API Response:', data); // Debug log
      
      // Validate and ensure stats structure exists with proper defaults
      if (data && data.hasGames && data.stats) {
        // Ensure winRate exists
        if (data.stats.winRate === undefined || data.stats.winRate === null) {
          data.stats.winRate = 0;
        }
        // Ensure timelineData exists
        if (!data.stats.timelineData) {
          data.stats.timelineData = [];
        }
        // Ensure championPerformanceData exists
        if (!data.stats.championPerformanceData) {
          data.stats.championPerformanceData = [];
        }
        // Ensure mostPlayedChampion has winRate if it exists
        if (data.stats.mostPlayedChampion && (data.stats.mostPlayedChampion.winRate === undefined || data.stats.mostPlayedChampion.winRate === null)) {
          data.stats.mostPlayedChampion.winRate = 0;
        }
      }
      
      setResult(data);
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Failed to generate. Try again later.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-logo">Rift Rewind</div>
      </header>

      {!result && (
        <div className="hero-container">
          <div className="dotted-pattern"></div>
          <div className="hero-bg"></div>
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="hero-heading">
              <span className="hero-heading-highlight">Rift Rewind</span><br />
              <span className="hero-heading-highlight">2025</span>
            </h1>
            <p className="hero-description">
              Unlock your personalized League of Legends year-end insights
            </p>
            <form onSubmit={onSubmit} className="hero-form">
              <input
                type="text"
                placeholder="GameName#TagLine"
                value={riotId}
                onChange={e => setRiotId(e.target.value)}
                className="hero-input"
              />
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="hero-button"
              >
                {loading ? (
                  <>
                    <Sparkles size={18} style={{ display: 'inline', marginRight: '8px' }} />
                    Processing…
                  </>
                ) : (
                  <>
                    <Gamepad2 size={18} style={{ display: 'inline', marginRight: '8px' }} />
                    Generate Rewind
                  </>
                )}
              </motion.button>
            </form>
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {error}
              </motion.div>
            )}
            {loading && (
              <motion.div
                className="loading-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.div
                  className="loading-spinner"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                <div className="loading-text">Summoning your agent…</div>
                <div className="loading-subtext">Analyzing your matches and crafting your story</div>
              </motion.div>
            )}
          </motion.div>
        </div>
      )}
      {result && <Slides data={result} riotId={riotId} onReset={() => { setResult(null); setRiotId(''); }} />}
    </div>
  );
}

function Slides({ data, riotId, onReset }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Build slides dynamically
  const hasAchievements = data.hasGames && data.stats && data.stats.achievements && data.stats.achievements.length > 0;
  // Always show charts if user has games, even if timelineData is empty (it will show placeholder)
  const hasCharts = data.hasGames && data.stats && data.stats.timelineData !== undefined;
  
  const baseSlides = [
    { title: 'Playstyle Discovery', content: data.archetype, icon: Target, color: '#667eea' },
    ...(data.hasGames ? [{ title: 'Your Stats', content: null, icon: TrendingUp, color: '#f093fb', isStats: true, stats: data.stats }] : []),
    ...(hasAchievements ? [{ title: 'Achievements', content: null, icon: Trophy, color: '#ffd700', isAchievements: true, achievements: data.stats.achievements }] : []),
    ...(hasCharts ? [{ title: 'Performance Charts', content: null, icon: TrendingUp, color: '#9b59b6', isCharts: true, stats: data.stats }] : []),
    { title: 'Your Coach Tip', content: data.coachTip, icon: Lightbulb, color: '#4facfe' },
    { title: 'Highlight Story', content: data.highlight, icon: Star, color: '#43e97b' },
    { title: 'Season Headline', content: data.headline, icon: Trophy, color: '#fa709a' },
    { title: 'Share Your Rewind', content: null, icon: Share2, color: '#ff6b6b', isShare: true }
  ];
  
  const slides = baseSlides.map((slide, index) => ({ ...slide, id: index }));

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const shareText = data.hasGames 
    ? `My 2025 Rift Rewind: ${data.headline}\n\n${data.archetype}\n\n${data.coachTip}\n\n${data.highlight}\n\n#RiftRewind2025`
    : `My 2025 Rift Rewind: ${data.headline}\n\n${data.archetype}\n\n${data.coachTip}\n\n${data.highlight}\n\nStart your journey in the Rift! #RiftRewind2025`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My 2025 Rift Rewind',
          text: shareText,
          url: window.location.href
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareText);
      alert('Copied to clipboard!');
    }
  };

  const IconComponent = slides[currentSlide]?.icon || Target;

  return (
    <div className="slides-container">
      {/* Background Pattern for Slides */}
      <div className="dotted-pattern" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}></div>
      
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 100, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -100, scale: 0.95 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="slide-card"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
            className="slide-icon"
          >
            <IconComponent size={64} color={slides[currentSlide].color} />
          </motion.div>
          <h2 className="slide-title">
            {slides[currentSlide].title}
          </h2>
          {slides[currentSlide].isShare ? (
            <div>
              <p className="slide-content" style={{ marginBottom: '30px' }}>
                Share your 2025 Rift Rewind with friends!
              </p>
              <div className="share-buttons">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className="share-button primary"
                >
                  <Share2 size={18} />
                  Share
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const text = shareText;
                    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
                    window.open(twitterUrl, '_blank');
                  }}
                  className="share-button twitter"
                >
                  <Share2 size={18} />
                  Tweet
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onReset}
                  className="share-button"
                >
                  <ChevronLeft size={18} />
                  New Rewind
                </motion.button>
              </div>
            </div>
          ) : slides[currentSlide].isStats ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}
            >
              {slides[currentSlide].stats && data.hasGames ? (
                <div style={{ display: 'grid', gap: '24px' }}>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-value">{slides[currentSlide].stats.totalGames}</div>
                      <div className="stat-label">Games</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{slides[currentSlide].stats.winRate ? (slides[currentSlide].stats.winRate * 100).toFixed(0) : 0}%</div>
                      <div className="stat-label">Win Rate</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{slides[currentSlide].stats.avgKDA ? slides[currentSlide].stats.avgKDA.toFixed(2) : 'N/A'}</div>
                      <div className="stat-label">Avg KDA</div>
                    </div>
                  </div>
                  
                  {slides[currentSlide].stats.mostPlayedChampion && (
                    <div className="glass-card">
                      <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '10px', color: '#3b82f6' }}>🏆 Most Played Champion</div>
                      <div style={{ fontSize: '1.3em', fontWeight: 'bold', color: '#ffffff', marginBottom: '5px' }}>{slides[currentSlide].stats.mostPlayedChampion.name}</div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9em' }}>
                        {slides[currentSlide].stats.mostPlayedChampion.games} games • {slides[currentSlide].stats.mostPlayedChampion.winRate ? (slides[currentSlide].stats.mostPlayedChampion.winRate * 100).toFixed(0) : 0}% win rate
                      </div>
                    </div>
                  )}
                  
                  {slides[currentSlide].stats.bestGame && (
                    <div className="glass-card">
                      <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '10px', color: '#fbbf24' }}>⭐ Best Game Performance</div>
                      <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#ffffff', marginBottom: '5px' }}>{slides[currentSlide].stats.bestGame.champion || 'N/A'}</div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9em' }}>
                        {slides[currentSlide].stats.bestGame.kills || 0}/{slides[currentSlide].stats.bestGame.deaths || 0}/{slides[currentSlide].stats.bestGame.assists || 0} • 
                        {slides[currentSlide].stats.bestGame.kda ? `KDA: ${slides[currentSlide].stats.bestGame.kda.toFixed(2)}` : 'KDA: N/A'} • 
                        {slides[currentSlide].stats.bestGame.damage ? `${slides[currentSlide].stats.bestGame.damage.toLocaleString()} damage` : 'Damage: N/A'}
                      </div>
                    </div>
                  )}
                  
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-value">{slides[currentSlide].stats.totalKills}</div>
                      <div className="stat-label">Total Kills</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{slides[currentSlide].stats.totalDeaths}</div>
                      <div className="stat-label">Total Deaths</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{slides[currentSlide].stats.totalAssists}</div>
                      <div className="stat-label">Total Assists</div>
                    </div>
                  </div>
                  
                  {/* Additional Statistics */}
                  {(slides[currentSlide].stats.avgGoldPerMinute > 0 || slides[currentSlide].stats.avgCSPerMinute > 0 || slides[currentSlide].stats.avgKillParticipation > 0) && (
                    <div className="glass-card" style={{ marginTop: '20px' }}>
                      <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '15px', color: '#3b82f6' }}>📊 Advanced Statistics</div>
                      <div className="stats-grid">
                        {slides[currentSlide].stats.avgGoldPerMinute > 0 && (
                          <div className="stat-card">
                            <div className="stat-value">{slides[currentSlide].stats.avgGoldPerMinute.toFixed(0)}</div>
                            <div className="stat-label">Avg GPM</div>
                          </div>
                        )}
                        {slides[currentSlide].stats.avgCSPerMinute > 0 && (
                          <div className="stat-card">
                            <div className="stat-value">{slides[currentSlide].stats.avgCSPerMinute.toFixed(1)}</div>
                            <div className="stat-label">Avg CS/min</div>
                          </div>
                        )}
                        {slides[currentSlide].stats.avgKillParticipation > 0 && (
                          <div className="stat-card">
                            <div className="stat-value">{slides[currentSlide].stats.avgKillParticipation.toFixed(0)}%</div>
                            <div className="stat-label">Kill Participation</div>
                          </div>
                        )}
                        {slides[currentSlide].stats.longestWinStreak > 0 && (
                          <div className="stat-card">
                            <div className="stat-value">{slides[currentSlide].stats.longestWinStreak}</div>
                            <div className="stat-label">Best Win Streak</div>
                          </div>
                        )}
                        {slides[currentSlide].stats.pentakills > 0 && (
                          <div className="stat-card">
                            <div className="stat-value">🔥 {slides[currentSlide].stats.pentakills}</div>
                            <div className="stat-label">Pentakills</div>
                          </div>
                        )}
                        {slides[currentSlide].stats.perfectGames > 0 && (
                          <div className="stat-card">
                            <div className="stat-value">✨ {slides[currentSlide].stats.perfectGames}</div>
                            <div className="stat-label">Perfect Games</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '3em', marginBottom: '20px' }}>🎮</div>
                  <h3 style={{ fontSize: '1.5em', marginBottom: '15px', color: '#3b82f6' }}>Your Stats Are Loading!</h3>
                  <p style={{ fontSize: '1.1em', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                    Play some games and come back to see your personalized statistics, 
                    including your win rate, KDA, most played champions, and best performances!
                  </p>
                  <p style={{ fontSize: '0.9em', color: 'rgba(255, 255, 255, 0.5)', marginTop: '20px' }}>
                    Your stats will appear here once you've played matches in League of Legends.
                  </p>
                </div>
              )}
            </motion.div>
          ) : slides[currentSlide].isAchievements ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}
            >
              <div className="achievement-grid">
                {slides[currentSlide].achievements.map((achievement, idx) => {
                  const tierColors = {
                    legendary: { gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', border: 'rgba(255, 71, 87, 0.5)' },
                    epic: { gradient: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)', border: 'rgba(95, 61, 196, 0.5)' },
                    rare: { gradient: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)', border: 'rgba(5, 117, 230, 0.5)' }
                  };
                  const colors = tierColors[achievement.tier] || tierColors.rare;
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                      className="achievement-card"
                      style={{
                        background: colors.gradient,
                        borderColor: colors.border
                      }}
                    >
                      <div className="achievement-icon">{achievement.icon}</div>
                      <div className="achievement-name">{achievement.name}</div>
                      <div className="achievement-description">{achievement.description}</div>
                      <div className="achievement-tier">{achievement.tier}</div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ) : slides[currentSlide].isCharts ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}
            >
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* Win Rate Trend */}
                {slides[currentSlide].stats.timelineData && slides[currentSlide].stats.timelineData.length > 0 ? (
                  <div className="chart-container">
                    <h3 className="chart-title">Win Rate Over Games</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={slides[currentSlide].stats.timelineData.map((d, i) => {
                        const gamesUpToNow = slides[currentSlide].stats.timelineData.slice(0, i + 1);
                        const winsUpToNow = gamesUpToNow.filter(g => g.win === true).length;
                        const totalGames = gamesUpToNow.length;
                        const winRate = totalGames > 0 ? (winsUpToNow / totalGames) * 100 : 0;
                        return {
                          game: i + 1,
                          winRate: winRate
                        };
                      })}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                        <XAxis dataKey="game" stroke="rgba(255, 255, 255, 0.6)" />
                        <YAxis domain={[0, 100]} stroke="rgba(255, 255, 255, 0.6)" />
                        <Tooltip 
                          formatter={(value) => value !== undefined && value !== null ? `${Number(value).toFixed(1)}%` : '0%'}
                          contentStyle={{ background: 'rgba(10, 10, 10, 0.9)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', color: '#ffffff' }}
                        />
                        <Line type="monotone" dataKey="winRate" stroke="#3b82f6" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="chart-container">
                    <h3 className="chart-title">Win Rate Over Games</h3>
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                      Chart data will appear as you play more games
                    </div>
                  </div>
                )}
                
                {/* KDA Progression */}
                {slides[currentSlide].stats.timelineData && slides[currentSlide].stats.timelineData.length > 0 ? (
                  <div className="chart-container">
                    <h3 className="chart-title">KDA Progression</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={slides[currentSlide].stats.timelineData.map((d, i) => ({ 
                        game: i + 1, 
                        kda: (d.kda !== undefined && d.kda !== null) ? d.kda : 0 
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                        <XAxis dataKey="game" stroke="rgba(255, 255, 255, 0.6)" />
                        <YAxis stroke="rgba(255, 255, 255, 0.6)" />
                        <Tooltip 
                          contentStyle={{ background: 'rgba(10, 10, 10, 0.9)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', color: '#ffffff' }}
                        />
                        <Line type="monotone" dataKey="kda" stroke="#43e97b" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="chart-container">
                    <h3 className="chart-title">KDA Progression</h3>
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                      Chart data will appear as you play more games
                    </div>
                  </div>
                )}
                
                {/* Champion Win Rate Chart */}
                {slides[currentSlide].stats.championPerformanceData && slides[currentSlide].stats.championPerformanceData.length > 0 ? (
                  <div className="chart-container">
                    <h3 className="chart-title">Top Champions by Win Rate</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={slides[currentSlide].stats.championPerformanceData.slice(0, 5).map(c => ({
                        name: c.name,
                        winRate: c.winRate ? parseFloat((c.winRate * 100).toFixed(0)) : 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                        <XAxis dataKey="name" stroke="rgba(255, 255, 255, 0.6)" />
                        <YAxis stroke="rgba(255, 255, 255, 0.6)" />
                        <Tooltip 
                          contentStyle={{ background: 'rgba(10, 10, 10, 0.9)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', color: '#ffffff' }}
                        />
                        <Bar dataKey="winRate" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="chart-container">
                    <h3 className="chart-title">Top Champions by Win Rate</h3>
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                      Champion data will appear as you play more games
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="slide-content"
              style={{ textAlign: 'center' }}
            >
              <p style={{ margin: 0, whiteSpace: 'pre-line' }}>
                {slides[currentSlide].content}
              </p>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="slide-navigation">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="nav-button"
        >
          <ChevronLeft size={18} />
          Previous
        </motion.button>

        <div className="slide-dots">
          {slides.map((_, idx) => (
            <motion.div
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`slide-dot ${currentSlide === idx ? 'active' : ''}`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="nav-button"
        >
          Next
          <ChevronRight size={18} />
        </motion.button>
      </div>
    </div>
  );
}

export default App;
